import React, { useState } from 'react';
import { Loan, Condition } from '../../types/conditions';
import { ConditionItem } from './ConditionItem';
import { Building2, User, Hash, ChevronDown, ChevronUp, AlertCircle, MessageSquare, ExternalLink } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface LoanCardProps {
  loan: Loan;
}
export function LoanCard({
  loan
}: LoanCardProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [conditions, setConditions] = useState<Condition[]>(loan.conditions);
  const displayedConditions = conditions.filter(c => {
    if (c.status !== 'Review') return false;
    const unreadCount = c.comments.filter(cm => cm.isRead === false).length;
    // Surface conditions that have something to review: documents present
    // OR unread comments. Once all comments are read on a doc-less condition,
    // it falls off the queue automatically.
    if (c.documents.length === 0 && unreadCount === 0) return false;
    return true;
  });
  const pendingConditionsCount = displayedConditions.length;
  // Only count unread comments from conditions actually shown in this queue,
  // so opening every visible condition fully clears the badge.
  const unreadCommentsCount = displayedConditions.reduce((sum, c) => sum + c.comments.filter(cm => cm.isRead === false).length, 0);
  const handleApprove = (conditionId: string) => {
    setConditions(prev => prev.map(c => c.id === conditionId ? {
      ...c,
      status: 'Approved'
    } : c));
  };
  const handleReopen = (conditionId: string) => {
    setConditions(prev => prev.map(c => c.id === conditionId ? {
      ...c,
      status: 'Review'
    } : c));
  };
  const handleReject = (conditionId: string, removedDocIds: string[], _reason: string, _sendEmail: boolean) => {
    setConditions(prev => prev.map(c => c.id === conditionId ? {
      ...c,
      status: 'Needs Info',
      documents: c.documents.filter(d => !removedDocIds.includes(d.id))
    } : c));
  };
  const handleMarkConditionRead = (conditionId: string) => {
    setConditions(prev => prev.map(c => c.id === conditionId ? {
      ...c,
      comments: c.comments.map(cm => cm.isRead === false ? {
        ...cm,
        isRead: true
      } : cm)
    } : c));
  };
  const handleAssociateDoc = (docId: string, fromConditionId: string, toConditionId: string, removeFromSource: boolean) => {
    setConditions(prev => {
      const source = prev.find(c => c.id === fromConditionId);
      const doc = source?.documents.find(d => d.id === docId);
      if (!doc) return prev;
      const newDoc = removeFromSource ? doc : {
        ...doc,
        id: `${doc.id}-copy-${Date.now()}`
      };
      return prev.map(c => {
        if (c.id === fromConditionId && removeFromSource) {
          const remainingDocs = c.documents.filter(d => d.id !== docId);
          const nextStatus = remainingDocs.length === 0 ? 'Requested' : c.status;
          return {
            ...c,
            documents: remainingDocs,
            status: nextStatus
          };
        }
        if (c.id === toConditionId) {
          if (c.documents.some(d => d.id === newDoc.id)) return c;
          const nextStatus = c.status === 'Requested' || c.status === 'Approved' || c.status === 'Needs Info' ? 'Review' : c.status;
          return {
            ...c,
            status: nextStatus,
            documents: [...c.documents, newDoc]
          };
        }
        return c;
      });
    });
  };
  if (displayedConditions.length === 0) {
    return null;
  }
  return <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden mb-6" data-id="element-207">
      {/* Loan Header */}
      <div className="p-5 border-b border-slate-200 bg-white cursor-pointer hover:bg-slate-50 transition-colors flex flex-col md:flex-row md:items-center justify-between gap-4" onClick={() => setIsExpanded(!isExpanded)} data-id="element-208">
        <div className="flex-1 min-w-0" data-id="element-209">
          <div className="flex items-center flex-wrap gap-x-3 gap-y-1.5 mb-2" data-id="element-210">
            <h3 className="text-lg font-bold text-slate-900 flex items-center" data-id="element-211">
              <User className="w-5 h-5 mr-2 text-[#0b5cab]" data-id="element-212" />
              {loan.borrowerName}
            </h3>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800" data-id="element-213">
              {loan.stage}
            </span>
            <a href="#" onClick={e => e.stopPropagation()} className="inline-flex items-center text-xs font-medium text-[#0b5cab] hover:text-[#094a89] hover:underline" aria-label={`Open loan page for ${loan.borrowerName}`} data-id="element-214">
              <ExternalLink className="w-3.5 h-3.5 mr-1" data-id="element-215" />
              Open Loan
            </a>
          </div>

          <div className="flex flex-wrap items-center text-sm text-slate-500 gap-y-2 gap-x-6" data-id="element-216">
            <span className="flex items-center" data-id="element-217">
              <Hash className="w-4 h-4 mr-1.5 text-slate-400" data-id="element-218" />
              {loan.loanNumber}
            </span>
            <span className="flex items-center font-medium text-slate-700" data-id="element-219">
              ${loan.loanAmount.toLocaleString()}
            </span>
            <span className="flex items-center" data-id="element-220">
              <Building2 className="w-4 h-4 mr-1.5 text-slate-400" data-id="element-221" />
              {loan.propertyAddress}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between md:justify-end gap-3 flex-shrink-0" data-id="element-222">
          {unreadCommentsCount > 0 && <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-[#0b5cab]/10 text-[#0b5cab] border border-[#0b5cab]/30" title={`${unreadCommentsCount} unread comment${unreadCommentsCount !== 1 ? 's' : ''}`} data-id="element-223">
              <MessageSquare className="w-4 h-4 mr-1.5" data-id="element-224" />
              {unreadCommentsCount} Unread
            </span>}
          {pendingConditionsCount > 0 ? <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-50 text-amber-700 border border-amber-200" data-id="element-225">
              <AlertCircle className="w-4 h-4 mr-1.5" data-id="element-226" />
              {pendingConditionsCount} Action Item
              {pendingConditionsCount !== 1 ? 's' : ''}
            </span> : <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-50 text-green-700 border border-green-200" data-id="element-227">
              All Clear
            </span>}
          <button className="text-slate-400 hover:text-slate-600 p-1 rounded-full hover:bg-slate-100 transition-colors" aria-label={isExpanded ? 'Collapse loan' : 'Expand loan'} data-id="element-228">
            {isExpanded ? <ChevronUp className="w-6 h-6" data-id="element-229" /> : <ChevronDown className="w-6 h-6" data-id="element-230" />}
          </button>
        </div>
      </div>

      <AnimatePresence data-id="element-231">
        {isExpanded && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="bg-slate-50/50 p-5" data-id="element-232">
            <div className="space-y-4" data-id="element-233">
              {displayedConditions.map(condition => {
            const siblings = conditions.filter(c => c.id !== condition.id).map(c => ({
              id: c.id,
              title: c.title,
              category: c.category,
              status: c.status
            }));
            return <ConditionItem key={condition.id} condition={condition} siblingConditions={siblings} onApprove={handleApprove} onReopen={handleReopen} onReject={handleReject} onAssociateDoc={handleAssociateDoc} onMarkRead={handleMarkConditionRead} data-id="element-234" />;
          })}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}